/**
 * @fileoverview Owner voucher claims drill-down — who claimed/redeemed a given
 * gift or promo. Shows a claim/redeem counter plus the per-customer list with
 * search + CSV export. Reused by the admin owner-twin via OwnerScopeProvider.
 */

import React, { useState, useCallback, useRef } from 'react';
import { ScrollView, View, Text, StyleSheet, TextInput, TouchableOpacity, Linking, Platform } from 'react-native';
import { useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { useOwnerScope } from '../../../../src/context/OwnerScopeContext';
import { getToken } from '../../../../src/api/client';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useToast } from '../../../../src/components/Toast';
import { useTheme } from '../../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, type ColorTheme } from '../../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../../src/utils/format';

interface ClaimRow {
  userId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  claimedAt: string | null;
  redeemCount: number;
  lastRedeemedAt: string | null;
  totalDiscountCents: number;
  signedUpViaThisQr?: boolean;
}
interface ClaimsResponse {
  voucher: {
    id: string; code: string; kind: 'gift' | 'promo' | 'points_redemption';
    discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell'; percentOff: number | null;
    tierName: string | null; status: string; redemptionLimit: number; redemptionsUsed: number;
    /** Human label for multi-select applicability rows; null on legacy rows. */
    appliesToLabel?: string | null;
    recipientEmail: string | null; expiresAt: string | null;
  };
  summary: {
    claimedCount: number; redeemedCount: number; redemptionLimit: number; totalDiscountCents: number;
    /** Customers with redeemCount > 0 — actual washes. redeemedCount counts claim-reserved slots for QR vouchers. */
    usedCount?: number;
    scanCount?: number; anonymousScanCount?: number; lastScanAt?: string | null; signupCount?: number;
  };
  claims: ClaimRow[];
  claimsTotal?: number;
  signups?: { userId: string; name: string | null; email: string | null; signedUpAt: string }[];
}

function offerLabel(v: ClaimsResponse['voucher']): string {
  if (v.discountKind === 'free_wash' || (v.discountKind === 'percent' && v.percentOff === 100)) {
    if (v.appliesToLabel) return `Free wash · ${v.appliesToLabel}`;
    return v.tierName ? `Free ${v.tierName} wash` : 'Free wash';
  }
  // free_minutes/free_vend: the claims endpoint doesn't return freeMinutes/
  // freeVendCents, so the label stays generic; appliesToLabel carries scope.
  if (v.discountKind === 'free_minutes') {
    return v.appliesToLabel ? `Free minutes · ${v.appliesToLabel}` : 'Free self-serve minutes';
  }
  if (v.discountKind === 'free_vend') return 'Vending credit';
  if (v.discountKind === 'free_upsell') return 'Free add-on with a wash';
  // credit: the claims endpoint doesn't return creditCents; generic label.
  if (v.discountKind === 'credit') return 'Gift credit (deposited at claim)';
  if (v.appliesToLabel) return `${v.percentOff}% off · ${v.appliesToLabel}`;
  return `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function VoucherClaimsScreen({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const scope = useOwnerScope();
  const params = useLocalSearchParams<{ id: string }>();
  const id = entityId ?? params.id;

  const [data, setData] = useState<ClaimsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const searchRef = useRef('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  /** Server-side chip filter (?used=1) — applied before pagination so totals
   *  span all pages. Ref mirrors state for use inside fetchClaims (same
   *  pattern as searchRef). */
  const [usedOnly, setUsedOnly] = useState(false);
  const usedOnlyRef = useRef(false);

  const fetchClaims = useCallback((p = 1) => {
    if (!id) return;
    setLoading(true);
    const params = new URLSearchParams({ page: String(p), limit: '10' });
    if (searchRef.current.trim()) params.set('search', searchRef.current.trim());
    if (usedOnlyRef.current) params.set('used', '1');
    scope.api.get<ClaimsResponse>(`/api/vouchers/${id}/claims?${params}`)
      .then(d => { setData(d); setPage(p); })
      .catch((err: any) => toast.show({ type: 'error', title: 'Error', message: err.message }))
      .finally(() => setLoading(false));
  }, [id, scope, toast]);

  useFocusEffect(useCallback(() => { fetchClaims(1); }, [fetchClaims]));

  const setUsedFilter = (v: boolean) => {
    usedOnlyRef.current = v;
    setUsedOnly(v);
    fetchClaims(1);
  };

  const pageRows = data?.claims ?? [];
  const filtered = pageRows;
  const claimsTotal = data?.claimsTotal ?? pageRows.length;
  const totalPages = Math.max(1, Math.ceil(claimsTotal / 10));

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const { downloadFile } = await import('../../../../src/utils/fileExport');
      const apiBase = (process.env.EXPO_PUBLIC_API_URL as string | undefined) || '';
      const path = scope.api.scopePath(`/api/vouchers/${id}/claims?format=csv`);
      const headers: Record<string, string> = {};
      if (Platform.OS !== 'web') {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      await downloadFile(`${apiBase}${path}`, `voucher-${data.voucher.code}-claims.csv`,
        Object.keys(headers).length > 0 ? headers : undefined);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Export failed', message: err?.message });
    } finally { setExporting(false); }
  };

  if (loading && !data) {
    return <View style={styles.loading}><Text style={{ color: colors.textSecondary }}>Loading claims…</Text></View>;
  }
  if (!data) {
    return <View style={styles.loading}><Text style={{ color: colors.textSecondary }}>Voucher not found.</Text></View>;
  }

  const v = data.voucher;
  const s = data.summary;
  const kindLabel = v.kind === 'promo' ? 'Promo' : 'Gift';

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={760}>
        <Card style={{ marginBottom: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <Ionicons name={v.kind === 'promo' ? 'pricetag' : 'gift'} size={16} color={colors.primary} />
            <Text style={styles.kindBadge}>{kindLabel}</Text>
            <Text style={[styles.statusBadge, { color: v.status === 'active' ? colors.success : colors.textSecondary }]}>· {v.status}</Text>
          </View>
          <Text style={styles.title}>{offerLabel(v)}</Text>
          <Text style={styles.code}>Code {v.code}{v.recipientEmail ? ` · ${v.recipientEmail}` : ''}</Text>

          <View style={styles.statStrip}>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{s.scanCount ?? 0}</Text>
              <Text style={styles.statLabel}>Scans</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{s.claimedCount}</Text>
              <Text style={styles.statLabel}>Claimed</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{s.redeemedCount}{s.redemptionLimit > 1 ? `/${s.redemptionLimit}` : ''}</Text>
              <Text style={styles.statLabel}>Redeemed</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{s.usedCount ?? 0}</Text>
              <Text style={styles.statLabel}>Used at wash</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{s.signupCount ?? 0}</Text>
              <Text style={styles.statLabel}>Signups</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>${(s.totalDiscountCents / 100).toFixed(2)}</Text>
              <Text style={styles.statLabel}>Total discount</Text>
            </View>
          </View>
          {(s.scanCount ?? 0) > 0 && (
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
              {s.anonymousScanCount ?? 0} of {s.scanCount} scans were signed-out visitors
              {s.lastScanAt ? ` · last scan ${fmtDateTime(s.lastScanAt)}` : ''}
            </Text>
          )}
        </Card>

        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm }}>
          <Text style={styles.sectionTitle}>Customers</Text>
          <Button
            title={exporting ? '…' : 'Export CSV'}
            variant="outline"
            onPress={handleExportCsv}
            icon={<Ionicons name="download-outline" size={16} color={colors.primary} />}
            disabled={exporting || (claimsTotal === 0 && !search)}
          />
        </View>

        <View style={styles.searchWrap}>
          <Ionicons name="search" size={16} color={colors.textLight} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by name, email, or phone"
            placeholderTextColor={colors.textLight}
            value={search}
            onChangeText={t => { searchRef.current = t; setSearch(t); }}
            onSubmitEditing={() => fetchClaims(1)}
            returnKeyType="search"
            autoCapitalize="none"
            autoCorrect={false}
          />
          {search ? (
            <TouchableOpacity onPress={() => { searchRef.current = ''; setSearch(''); fetchClaims(1); }}>
              <Ionicons name="close-circle" size={16} color={colors.textLight} />
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={styles.chipRow}>
          <TouchableOpacity
            style={[styles.chip, !usedOnly && styles.chipActive]}
            onPress={() => setUsedFilter(false)}
          >
            <Text style={[styles.chipText, !usedOnly && styles.chipTextActive]}>All</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.chip, usedOnly && styles.chipActive]}
            onPress={() => setUsedFilter(true)}
          >
            <Text style={[styles.chipText, usedOnly && styles.chipTextActive]}>Used at wash</Text>
          </TouchableOpacity>
        </View>

        <Card style={{ paddingVertical: 0, marginTop: spacing.sm }}>
          {filtered.length === 0 ? (
            <Text style={styles.empty}>
              {usedOnly
                ? 'No customers have used it at the wash yet.'
                : search ? 'No customers match.' : `No one has claimed this ${kindLabel.toLowerCase()} yet.`}
            </Text>
          ) : (
            filtered.map((r, idx) => (
              <View key={r.userId} style={[styles.row, idx === filtered.length - 1 && { borderBottomWidth: 0 }]}>
                <View style={{ flex: 1, paddingRight: spacing.sm }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text style={styles.name}>{titleCase(r.name) || '(no name)'}</Text>
                    {r.signedUpViaThisQr && (
                      <View style={{ backgroundColor: colors.success + '20', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                        <Text style={{ fontSize: 10, color: colors.success, fontWeight: '700' }}>Signed up via this QR</Text>
                      </View>
                    )}
                  </View>
                  {r.email && (
                    <TouchableOpacity onPress={() => Linking.openURL(`mailto:${r.email}`)}>
                      <Text style={styles.contact}>{r.email}</Text>
                    </TouchableOpacity>
                  )}
                  {r.phone && (
                    <TouchableOpacity onPress={() => Linking.openURL(`tel:${r.phone}`)}>
                      <Text style={styles.contact}>{formatPhone(r.phone)}</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={styles.metaStrong}>
                    {r.redeemCount > 0 ? `Redeemed ${r.redeemCount}×` : 'Not yet redeemed'}
                  </Text>
                  <Text style={styles.metaSub}>Claimed {fmtDateTime(r.claimedAt)}</Text>
                  {r.redeemCount > 0 && (
                    <Text style={styles.metaSub}>Last {fmtDateTime(r.lastRedeemedAt)} · ${(r.totalDiscountCents / 100).toFixed(2)}</Text>
                  )}
                </View>
              </View>
            ))
          )}
          {totalPages > 1 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, paddingVertical: spacing.sm }}>
              <TouchableOpacity disabled={page <= 1} onPress={() => fetchClaims(page - 1)} hitSlop={8}>
                <Ionicons name="chevron-back" size={20} color={page <= 1 ? colors.border : colors.primary} />
              </TouchableOpacity>
              <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{page}/{totalPages} · {claimsTotal} customers</Text>
              <TouchableOpacity disabled={page >= totalPages} onPress={() => fetchClaims(page + 1)} hitSlop={8}>
                <Ionicons name="chevron-forward" size={20} color={page >= totalPages ? colors.border : colors.primary} />
              </TouchableOpacity>
            </View>
          )}
        </Card>
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  kindBadge: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color: c.primary, textTransform: 'uppercase', letterSpacing: 0.5 },
  statusBadge: { fontSize: fontSize.xs, fontWeight: fontWeight.medium as any },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: 4 },
  code: { fontSize: fontSize.sm, color: c.textLight, marginTop: 2 },

  statStrip: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, rowGap: spacing.sm, marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  stat: { alignItems: 'flex-start', minWidth: 64 },
  statValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  statLabel: { fontSize: 10, color: c.textLight, textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 1 },

  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text },
  searchWrap: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6 },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  chipRow: { flexDirection: 'row', gap: 6, marginTop: spacing.sm },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: 5, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.border, backgroundColor: c.surfaceSecondary },
  chipActive: { borderColor: c.primary, backgroundColor: c.primary + '15' },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary },
  chipTextActive: { color: c.primary, fontWeight: fontWeight.semibold as any },

  row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  contact: { fontSize: fontSize.xs, color: c.primary, marginTop: 2 },
  metaStrong: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color: c.text },
  metaSub: { fontSize: 11, color: c.textLight, marginTop: 1 },
  empty: { textAlign: 'center', color: c.textLight, padding: spacing.lg, fontSize: fontSize.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
